import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTeacherTrials } from '@/hooks/useTeacherTrials';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClipboardEdit, Loader2 } from 'lucide-react';

const statusColor = (status: string | null) => {
  switch (status) {
    case 'ปิดการขายเรียบร้อย': return 'bg-green-100 text-green-800';
    case 'ครูส่งผลประเมินแล้ว': return 'bg-blue-100 text-blue-800';
    case 'รอครูประเมิน': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const statusLabel = (status: string | null, t: (k: string) => string) => {
  switch (status) {
    case 'ปิดการขายเรียบร้อย': return t('trials.status.closedSuccess');
    case 'ครูส่งผลประเมินแล้ว': return t('trials.status.submitted');
    case 'รอครูประเมิน': return t('trials.status.pending');
    default: return status || '-';
  }
};

export default function TeacherTrialsPage() {
  const { t } = useTranslation();
  const { trials, isLoading } = useTeacherTrials();

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('trials.pageTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : trials.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">{t('trials.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('trials.colDate')}</TableHead>
                    <TableHead>{t('trials.colNickname')}</TableHead>
                    <TableHead>{t('trials.colName')}</TableHead>
                    <TableHead>{t('trials.colGrade')}</TableHead>
                    <TableHead>{t('trials.colSubject')}</TableHead>
                    <TableHead>{t('trials.colStatus')}</TableHead>
                    <TableHead className="text-right">{t('trials.colAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trials.map((trial) => (
                    <TableRow key={trial.id}>
                      <TableCell className="whitespace-nowrap">{trial.class_date || '-'}</TableCell>
                      <TableCell>{trial.student_nickname || '-'}</TableCell>
                      <TableCell>{trial.student_name || '-'}</TableCell>
                      <TableCell>{trial.grade || '-'}</TableCell>
                      <TableCell>{trial.subject || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={statusColor(trial.status)}>
                          {statusLabel(trial.status, t)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/teacher-trials/${trial.id}`}>
                            <ClipboardEdit className="h-4 w-4 mr-1" />
                            {t('trials.evaluate')}
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
